import { inject, Injectable } from '@angular/core';
import { Auth, ConfirmationResult, RecaptchaVerifier, User, signInWithPhoneNumber, user, updateProfile, signOut, signInWithCustomToken, deleteUser, PhoneAuthProvider, reauthenticateWithCredential } from '@angular/fire/auth';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { UtilityService } from './utility.service';

@Injectable({
  providedIn: 'root'
})
export class FirebaseAuthService {
    private auth: Auth = inject(Auth);
    private firebaseFunctions: Functions = inject(Functions);
    private utilityService: UtilityService = inject(UtilityService);

    // Angular Auth Observables
    user$ = user(this.auth);
    // authState$ = authState(this.auth);
    // idToken$ = idToken(this.auth);

    confirmationResult: ConfirmationResult | undefined;
    recaptchaVerifier: RecaptchaVerifier | undefined;
    
    constructor() {}

    setRecaptchaVerifier(signInButtonId: string) {
        if (this.utilityService.deviceInfo) return;
        console.log('setRecaptchaVerifier', this.utilityService.deviceInfo);
        console.log('setRecaptchaVerifier', signInButtonId);
        if(this.recaptchaVerifier) return;
        try {
            // Check if the element exists first
            const element = document.getElementById(signInButtonId);
            if (!element) {
                console.warn(`Element with id '${signInButtonId}' not found. reCAPTCHA setup skipped.`);
                return;
            }

            this.recaptchaVerifier = new RecaptchaVerifier(this.auth, signInButtonId, {
                size: 'invisible',
                callback: () => {
                    // reCAPTCHA solved, allow signInWithPhoneNumber.
                    // callback();
                    console.log('callback');
                    const recaptchaElement = document.getElementById('recaptcha-container');
                    if(recaptchaElement) {
                        recaptchaElement.style.display = "none";
                    }
                }
            });
            console.log('recaptchaVerifier', this.recaptchaVerifier);
        } catch(error) {
            console.log('reCAPTCHA setup failed:', error);
            // Don't throw error, just log it and continue
        }
    }

    async loginWithPhoneNumber(phoneNumber: string) {
        try {
            if (!this.recaptchaVerifier) {
                console.warn('reCAPTCHA verifier not available. Phone login may not work properly.');
                // You might want to throw an error here or handle it differently
                throw new Error('reCAPTCHA not configured');
            }
            this.confirmationResult = await signInWithPhoneNumber(this.auth, phoneNumber, this.recaptchaVerifier as RecaptchaVerifier);
        } catch(error) {
            console.log('Phone login failed:', error);
            throw new Error('Login Failed');
        }
    }

    setConfirmationResultFromNativeLayerLogin(confirmationResult: ConfirmationResult) {
        this.confirmationResult = confirmationResult;
    }

    async signInWithToken(uid: string) {
        try {
            const createLoginCustomTokenFn = httpsCallable(this.firebaseFunctions, 'createLoginCustomToken');
            const response = await createLoginCustomTokenFn({idToken: uid});
            const {token} = response.data as {token: string};
            const userCredentials = await signInWithCustomToken(this.auth, token);
            return userCredentials;
        } catch(error) {
            console.log(error);
            throw new Error('OTP Verification Failed');
        }
    }

    async verifyOTP(otp: string) {
        try {
            const userCredentials = await this.confirmationResult?.confirm(otp);
            return userCredentials;
        } catch(error) {
            console.log(error);
            throw new Error('OTP Verification Failed');
        }
    }

    async updateUserProfile(user: User, {displayName, photoURL}: {displayName: string | null, photoURL: string | null}) {
        displayName = displayName || user.displayName || null;
        photoURL = photoURL || user.photoURL ||  null;
        try {
            await updateProfile(user, {displayName, photoURL});
        } catch(error) {
            console.log(error);
            throw new Error('Update User Profile Failed');
        }
    }

    async logout() {
        try {
            await signOut(this.auth);
        } catch(error) {
            console.log(error);
            throw new Error('Logout Failed');
        }
    }

    async sendVerificationCodeForReauth(phoneNumber: string): Promise<string> {
        try {
            // Set up recaptcha for re-authentication
            this.setRecaptchaVerifier('recaptcha-container');
            
            // Send verification code
            const confirmationResult = await signInWithPhoneNumber(this.auth, phoneNumber, this.recaptchaVerifier!);
            this.confirmationResult = confirmationResult;
            
            return confirmationResult.verificationId;
        } catch (error) {
            console.error('Failed to send verification code:', error);
            throw new Error('Failed to send verification code');
        }
    }

    async reauthenticateUser(verificationId: string, verificationCode: string): Promise<boolean> {
        try {
            const user = this.auth.currentUser;
            if (!user) {
                throw new Error('No user logged in');
            }

            // Create phone credential
            const credential = PhoneAuthProvider.credential(verificationId, verificationCode);
            
            // Re-authenticate the user using the credential
            await reauthenticateWithCredential(user, credential);
            return true;
        } catch (error) {
            console.error('Re-authentication failed:', error);
            return false;
        }
    }

    async deleteAccountWithReauth(phoneNumber: string, verificationId: string, verificationCode: string): Promise<{ success: boolean; needsReauth: boolean; message: string }> {
        try {
            const user = this.auth.currentUser;
            if (!user) {
                throw new Error('No user logged in');
            }

            // First, re-authenticate the user
            const reauthSuccess = await this.reauthenticateUser(verificationId, verificationCode);
            if (!reauthSuccess) {
                return { success: false, needsReauth: true, message: 'Re-authentication failed. Please try again.' };
            }

            // Now delete the account
            // await deleteUser(user);
            await this.logout();
            return { success: true, needsReauth: false, message: 'Account deleted successfully' };
            
        } catch (error: unknown) {
            console.log('Delete account with re-auth error:', error);
            
            const firebaseError = error as { code?: string; message?: string };
            if (firebaseError.code === 'auth/credential-too-old-login-again' || 
                firebaseError.code === 'auth/requires-recent-login') {
                return { success: false, needsReauth: true, message: 'Re-authentication required. Please try again.' };
            }
            
            return { success: false, needsReauth: false, message: 'Account deletion failed: ' + firebaseError.message };
        }
    }
}